import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listBroadcastRecipients,
  getBroadcast,
} from "@/lib/broadcasts.functions";
import { ArrowLeft, RefreshCw, CheckCircle2, XCircle, Clock, UserX, Download, Search, ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useMemo, useState } from "react";

export const Route = createFileRoute("/admin_/broadcasts_/$id/delivery")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Broadcast delivery status — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: DeliveryStatusPage,
});

function DeliveryStatusPage() {
  const { id } = Route.useParams();
  const recipientsFn = useServerFn(listBroadcastRecipients);
  const bcFn = useServerFn(getBroadcast);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const { data: bc } = useQuery({
    queryKey: ["broadcast", id],
    queryFn: () => bcFn({ data: { id } }),
  });

  const { data, isLoading, isFetching, dataUpdatedAt, refetch } = useQuery({
    queryKey: ["broadcast-delivery", id],
    queryFn: () => recipientsFn({ data: { id } }),
    refetchInterval: autoRefresh ? 3000 : false,
    refetchIntervalInBackground: false,
  });

  const summary = data?.summary;
  const rows = data?.rows ?? [];
  const total = summary?.total ?? 0;
  const sent = summary?.sent ?? 0;
  const failed = summary?.failed ?? 0;
  const pending = summary?.pending ?? 0;
  const unsubscribed = summary?.unsubscribed ?? 0;
  const processed = sent + failed + unsubscribed;
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const isActive = pending > 0 || bc?.broadcast?.status === "sending" || bc?.broadcast?.status === "scheduled";

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:py-10">
      <Link
        to="/admin/broadcasts"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Broadcasts
      </Link>

      <header className="mt-4 rounded-3xl border border-gold/30 bg-gradient-to-br from-cream/70 via-background to-cream/40 p-6 shadow-[var(--shadow-gold)] sm:p-8">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-medium uppercase tracking-wider text-[var(--gold-deep)]">
              Delivery status
            </div>
            <h1 className="mt-2 truncate font-display text-2xl tracking-tight text-foreground sm:text-3xl">
              {bc?.broadcast?.subject ?? "Broadcast"}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="rounded-full border border-border bg-card px-2 py-0.5 font-medium">
                {bc?.broadcast?.status ?? "—"}
              </span>
              <span>
                {isFetching ? "Refreshing…" : dataUpdatedAt ? `Updated ${new Date(dataUpdatedAt).toLocaleTimeString()}` : ""}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} /> Refresh
            </button>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh (3s)
            </label>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-6">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {processed} / {total} processed
            </span>
            <span className="tabular-nums">{pct}%</span>
          </div>
          <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                isActive
                  ? "bg-gradient-to-r from-[var(--gold-deep)] to-gold"
                  : failed > 0
                    ? "bg-amber-500"
                    : "bg-emerald-500"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </header>

      {/* Stat cards */}
      <section className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          icon={<CheckCircle2 className="h-4 w-4" />}
          label="Sent"
          value={sent}
          tone="emerald"
        />
        <StatCard
          icon={<XCircle className="h-4 w-4" />}
          label="Failed"
          value={failed}
          tone="red"
        />
        <StatCard
          icon={<Clock className="h-4 w-4" />}
          label="Pending"
          value={pending}
          tone="amber"
          pulse={pending > 0}
        />
        <StatCard
          icon={<UserX className="h-4 w-4" />}
          label="Unsubscribed"
          value={unsubscribed}
          tone="muted"
        />
      </section>

      {/* Per-recipient log */}
      <RecipientLog rows={rows} isLoading={isLoading} broadcastId={id} subject={bc?.broadcast?.subject ?? ""} />
    </div>
  );
}

type LogRow = {
  id: string;
  email: string;
  status: string;
  sent_at: string | null;
  error: string | null;
  created_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  unsubscribed_at: string | null;
};

type SortKey = "email" | "status" | "created_at" | "sent_at";

function RecipientLog({
  rows,
  isLoading,
  broadcastId,
  subject,
}: {
  rows: LogRow[];
  isLoading: boolean;
  broadcastId: string;
  subject: string;
}) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("sent_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const PAGE_SIZE = 50;

  const statuses = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) set.add(r.status);
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = rows;
    if (statusFilter !== "all") out = out.filter((r) => r.status === statusFilter);
    if (q) out = out.filter((r) => r.email.toLowerCase().includes(q) || (r.error ?? "").toLowerCase().includes(q));
    const dir = sortDir === "asc" ? 1 : -1;
    out = [...out].sort((a, b) => {
      const av = (a as any)[sortKey] ?? "";
      const bv = (b as any)[sortKey] ?? "";
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return out;
  }, [rows, query, statusFilter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      setSortDir(k === "email" || k === "status" ? "asc" : "desc");
    }
    setPage(0);
  }

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function deriveErrorCode(status: string, error: string | null): string {
    if (status !== "failed" && status !== "bounced" && status !== "suppressed") return "";
    if (status === "suppressed") return "suppressed";
    if (status === "bounced") return "bounced";
    const e = (error ?? "").toLowerCase();
    if (!e) return "unknown";
    if (/rate[_ ]?limit|too[_ ]many[_ ]requests|\b429\b/.test(e)) return "rate_limit";
    if (/daily[_ ]?quota|quota[_ ]?exceeded/.test(e)) return "quota_exceeded";
    if (/invalid[_ ]?(to|email|recipient)|not a valid email/.test(e)) return "invalid_email";
    if (/bounce/.test(e)) return "bounced";
    if (/suppress|unsubscrib/.test(e)) return "suppressed";
    if (/complain|spam/.test(e)) return "complaint";
    if (/timeout|timed out/.test(e)) return "timeout";
    if (/\b5\d{2}\b/.test(e)) return "http_5xx";
    if (/\b4\d{2}\b/.test(e)) return "http_4xx";
    if (/network|fetch failed|econn/.test(e)) return "network_error";
    return "error";
  }

  function exportCsv() {
    const header = ["email", "status", "error_code", "queued_at", "sent_at", "opened_at", "clicked_at", "unsubscribed_at", "error"];
    const escape = (v: unknown) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(",")];
    for (const r of filtered) {
      lines.push(
        [
          r.email,
          r.status,
          deriveErrorCode(r.status, r.error),
          r.created_at ?? "",
          r.sent_at ?? "",
          r.opened_at ?? "",
          r.clicked_at ?? "",
          r.unsubscribed_at ?? "",
          r.error ?? "",
        ]
          .map(escape)
          .join(","),
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeSubject = (subject || broadcastId).replace(/[^a-z0-9-_]+/gi, "_").slice(0, 40);
    a.download = `broadcast-${safeSubject}-recipients.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }


  const fmt = (ts: string | null) =>
    ts ? new Date(ts).toLocaleString(undefined, { dateStyle: "short", timeStyle: "medium" }) : "—";

  return (
    <section className="mt-8">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <h2 className="font-display text-lg text-foreground">
          Per-recipient log{" "}
          <span className="text-sm font-normal text-muted-foreground">
            ({filtered.length.toLocaleString()}
            {filtered.length !== rows.length ? ` of ${rows.length.toLocaleString()}` : ""})
          </span>
        </h2>
        <button
          type="button"
          onClick={exportCsv}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-40"
        >
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute start-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(0);
            }}
            placeholder="Search email or error…"
            className="w-full rounded-lg border border-border bg-card py-1.5 ps-8 pe-3 text-sm placeholder:text-muted-foreground focus:border-gold/50 focus:outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
          className="rounded-lg border border-border bg-card px-2 py-1.5 text-sm"
        >
          <option value="all">All statuses</option>
          {statuses.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="h-40 animate-pulse rounded-2xl border border-border bg-card" />
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No recipients match this filter.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-start text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="w-6 px-2 py-2" />
                  <SortHeader label="Email" k="email" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Queued" k="created_at" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Sent / Attempted" k="sent_at" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <th className="px-4 py-2 font-medium">Failure reason</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => {
                  const hasError = !!r.error;
                  const isOpen = expanded.has(r.id);
                  return (
                    <Fragment key={r.id}>
                      <tr key={r.id} className="border-t border-border/60 align-top">
                        <td className="px-2 py-2">
                          {hasError ? (
                            <button
                              type="button"
                              onClick={() => toggleExpand(r.id)}
                              aria-label={isOpen ? "Collapse error" : "Expand error"}
                              className="rounded p-0.5 text-muted-foreground hover:bg-muted"
                            >
                              {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            </button>
                          ) : null}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{r.email}</td>
                        <td className="px-4 py-2">
                          <StatusPill status={r.status} />
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">
                          {fmt(r.created_at)}
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground tabular-nums">
                          {fmt(r.sent_at)}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {hasError ? (
                            <span className="line-clamp-1 max-w-md text-red-700">{r.error}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                      {hasError && isOpen && (
                        <tr key={`${r.id}-detail`} className="border-t border-border/40 bg-red-50/40">
                          <td />
                          <td colSpan={5} className="px-4 py-3">
                            <div className="grid gap-3 sm:grid-cols-2">
                              <div>
                                <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                  Full error
                                </div>
                                <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-md bg-background p-2 text-xs text-red-800">
{r.error}
                                </pre>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                <div>
                                  <span className="font-medium">Opened:</span> {fmt(r.opened_at)}
                                </div>
                                <div>
                                  <span className="font-medium">Clicked:</span> {fmt(r.clicked_at)}
                                </div>
                                <div>
                                  <span className="font-medium">Unsubscribed:</span> {fmt(r.unsubscribed_at)}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-border/60 px-4 py-2 text-xs text-muted-foreground">
            <span>
              Page {page + 1} of {totalPages} · {PAGE_SIZE} per page
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function SortHeader({
  label,
  k,
  sortKey,
  sortDir,
  onClick,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: "asc" | "desc";
  onClick: (k: SortKey) => void;
}) {
  const active = sortKey === k;
  return (
    <th className="px-4 py-2 font-medium">
      <button
        type="button"
        onClick={() => onClick(k)}
        className={`inline-flex items-center gap-1 hover:text-foreground ${active ? "text-foreground" : ""}`}
      >
        {label}
        {active ? <span aria-hidden>{sortDir === "asc" ? "↑" : "↓"}</span> : null}
      </button>
    </th>
  );
}

function StatCard({
  icon,
  label,
  value,
  tone,
  pulse,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  tone: "emerald" | "red" | "amber" | "muted";
  pulse?: boolean;
}) {
  const tones: Record<string, string> = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    red: "text-red-700 bg-red-50 border-red-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    muted: "text-muted-foreground bg-muted border-border",
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span
          className={`inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${tones[tone]}`}
        >
          {icon} {label}
        </span>
        {pulse && (
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
          </span>
        )}
      </div>
      <div className="mt-2 font-display text-2xl tabular-nums text-foreground sm:text-3xl">
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
    failed: "bg-red-50 text-red-700 border-red-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    unsubscribed: "bg-muted text-muted-foreground border-border",
    excluded: "bg-muted text-muted-foreground border-border",
    bounced: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span
      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${
        map[status] ?? "bg-muted text-muted-foreground border-border"
      }`}
    >
      {status}
    </span>
  );
}
